/* ============================================================
   NORTHSHEEP — PREVIOUS / NEXT HIDE

   Fills in the arrows rendered by snippets/ns-product-pager.liquid. Three
   things to know before reading:

   1. THE LIST COMES FROM THE GRID WHEN IT CAN. ns-grid-return.js records the
      ordered handles as the shopper leaves the collection — their sort, their
      filters, their hidden products already removed. Walking that list is the
      only version of "next" that matches what they were looking at.

   2. OTHERWISE THE COLLECTION ANSWERS FOR ITSELF. A shopper who landed from
      Google Shopping has no list, so the collection named in the breadcrumb
      is fetched once through ?view=ns-handles (handles only, ~7KB for 250,
      CDN-cached) and kept for the session. Never on the critical path: the
      arrows simply appear a moment later.

   3. THE LIST TRAVELS. Flipping to the next hide keeps the same list and
      moves the cursor, so a shopper can walk fifteen hides without going
      back — and the collection's return position follows them, so the
      breadcrumb back to the grid lands on the hide they are looking at now
      rather than the one they first clicked.
   ============================================================ */
(function () {
  'use strict';
  if (window.__nsProductPager) return;
  window.__nsProductPager = true;

  var LIST_KEY = 'northsheep_grid_list';
  var RETURN_KEY = 'northsheep_grid_return';
  var FETCH_PREFIX = 'northsheep_col_handles:';

  var SS = (function () {
    try {
      sessionStorage.setItem('__nspg__', '1');
      sessionStorage.removeItem('__nspg__');
      return true;
    } catch (e) {
      return false;
    }
  })();

  function readJSON(key) {
    if (!SS) return null;
    try {
      var raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  function writeJSON(key, v) {
    if (!SS) return;
    try {
      sessionStorage.setItem(key, JSON.stringify(v));
    } catch (e) {}
  }

  /* ---- stay at the same height when flipping ----

     Owner, 20 Sep 2026: "now if i scroll down a bit to allign better with the
     images it moves me back up everytime i click next product".

     Flipping through a rack, the shopper finds the height they want to look
     from — the photograph filling the screen, the header gone — and every
     arrow threw them back to the top of the next page to find it again. The
     pages are built the same way down to the pixel above the fold, so the
     offset means the same thing on the next hide as on this one.

     Only ever after an ARROW. An ordinary arrival at a product page starts at
     the top, as it should, and a back from the next hide is left to the
     browser, which already restores it exactly. The mark is consumed on the
     first load that sees it, so it can never apply twice. */
  var SCROLL_KEY = 'northsheep_pager_scroll';
  /* ONLY WHILE THE PRODUCT IS WHAT IS ON SCREEN (owner, 24 Sep 2026: "when i
     first load a product it jumps me all the way down to recently viewed
     section", desktop). The height is carried so the PHOTOGRAPH stays where
     the shopper put it. Scrolled down to the reviews or recently viewed, the
     arrow keys still flipped, and the next hide opened at that depth, which
     on the next page is its recently viewed row (measured: 6465 -> 6465, row
     at 6433). Below the product section neither the keys nor the height apply.
     An unknown layout keeps the old behaviour. */
  function productInView() {
    var info = document.querySelector("product-info[id^='MainProduct']") || document.querySelector('product-info');
    var sec = info && info.closest('.shopify-section');
    if (!sec) return true;
    return sec.getBoundingClientRect().bottom > window.innerHeight * 0.35;
  }
  /* THE MARK NAMES WHERE IT IS FOR (audit, 23 Sep 2026). It is written on
     pointerdown too, and a press that never became a click left it lying
     for the next product page opened within 30 seconds, from anywhere —
     measured: a press on "next", then a related product, which opened at 725
     instead of the top. Now it holds the arrow's destination, and the head
     (layout/theme.liquid) and restoreScroll below apply it only there. */
  function rememberScroll(to) {
    var y = productInView() ? Math.round(window.pageYOffset || 0) : 0;
    writeJSON(SCROLL_KEY, { y: y, ts: Date.now(), to: to || '' });
  }
  function isHere(to) {
    if (!to) return true;
    try {
      return decodeURIComponent(to) === decodeURIComponent(location.pathname);
    } catch (e) {
      return to === location.pathname;
    }
  }
  (function restoreScroll() {
    /* THE FIRST SCROLL HAS USUALLY HAPPENED ALREADY. An inline script in
       main-product.liquid, right after the gallery, does it before the page is
       first painted — this file is deferred, which is after. What is left here
       is the settle pass: re-applying for a moment in case the page finishes
       shorter than it started, and cleaning the mark up. */
    var early = window.__nsPagerRestore;
    var s = readJSON(SCROLL_KEY) || (early ? { y: early, ts: Date.now() } : null);
    if (!s) return;
    try {
      sessionStorage.removeItem(SCROLL_KEY);
    } catch (e) {}
    /* Stale marks are ignored rather than obeyed: a tab left open for an hour
       and then reloaded should not jump. So are marks meant for another hide. */
    if (!s.y || s.y < 40 || Date.now() - s.ts > 30000 || !isHere(s.to)) return;

    var stop = false;
    var deadline = Date.now() + 1500;
    function quit() {
      stop = true;
      window.removeEventListener('wheel', quit);
      window.removeEventListener('touchstart', quit);
      window.removeEventListener('keydown', quit);
    }
    window.addEventListener('wheel', quit, { passive: true });
    window.addEventListener('touchstart', quit, { passive: true });
    window.addEventListener('keydown', quit);

    /* The inline script claimed a minimum height so the scroll could land
       before the page had finished arriving. It is given back the moment the
       real content is taller than the claim — any earlier and the page would
       shrink under the shopper and take the scroll position with it. */
    function releaseHeight() {
      var doc = document.documentElement;
      if (!doc.style.minHeight) return;
      var claimed = parseInt(doc.style.minHeight, 10) || 0;
      doc.style.minHeight = '';
      if (doc.scrollHeight < claimed) {
        doc.style.minHeight = claimed + 'px';
        return;
      }
      /* Scroll anchoring goes back on with the height — it is the browser
         holding a reader's place while images load above them, and it should
         only have been off for the moment the page was being put in position. */
      doc.style.overflowAnchor = '';
    }

    /* ONLY EVER CORRECTS UPWARDS, AND ONLY UNTIL THE PAGE CAN HOLD THE
       POSITION ITSELF (owner: "it pushes the next product image a bit up and
       down so looks bit laggy").

       The first version re-applied the exact offset every 100ms for a second
       and a half. Meanwhile the browser's own scroll anchoring was nudging the
       position to keep the photograph still as things landed above it — so
       each correction undid an adjustment that was doing the right thing, and
       the picture rocked. Twice a second, for a second and a half.

       So: if the page has fallen SHORT of the position it is pulled back
       down, because that is the clamp this whole mechanism exists to beat. If
       it sits a little past it, that is anchoring holding the shopper's view
       steady and it is left alone. The moment the document is genuinely tall
       enough, the claim is released and this stops watching. */
    function apply() {
      if (stop) return;
      var doc = document.documentElement;
      var tallEnough = doc.scrollHeight >= s.y + window.innerHeight;
      if ((window.pageYOffset || 0) < s.y - 2) window.scrollTo(0, s.y);
      if (tallEnough) {
        releaseHeight();
        quit();
        return;
      }
      if (Date.now() < deadline) setTimeout(apply, 60);
      else {
        doc.style.minHeight = '';
        quit();
      }
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply);
    else apply();
  })();

  /* The pager row being drawn, and its parts. Set by start() — once as the
     page loads, and again when a colour swap (ns-colour-siblings.js) puts a
     different product's page in place without a page load. */
  var root = null;
  var handle = null;
  var prevEl = null;
  var nextEl = null;
  var countEl = null;
  var showCount = true;
  var generation = 0;

  /* The path this store serves products under, taken from the page we are
     standing on rather than assumed to be "/products/". A store served on a
     locale path answers at /en-gb/products/… and a hardcoded root would link
     every arrow to a 404. Dawn's window.routes does not carry a root, and the
     current URL always does. */
  var PRODUCT_BASE = (function () {
    var p = location.pathname;
    var i = p.indexOf('/products/');
    return i === -1 ? '/products/' : p.slice(0, i) + '/products/';
  })();
  function urlFor(h) {
    return PRODUCT_BASE + h;
  }

  function label(tpl, current, total) {
    return String(tpl || '')
      .replace('[current]', current)
      .replace('[total]', total);
  }

  /* ---- the peek: what the next hide looks like, on hover ----

     Owner, 20 Sep 2026: "can you show the image of the previous and next
     product on hoover in small?". On a catalogue where every piece is
     different, "next" is a question — this answers it before the click, which
     is the difference between flipping through a rack and opening doors.

     NOT FETCHED UNTIL SOMEBODY ASKS. The photo is pulled on the first hover
     or keyboard focus, from the same few-bytes view the favourites use
     (?view=ns-status, about 120 bytes plus the CDN image) — not on page load,
     where it would cost every visitor a request for something most of them
     never point at. Once fetched it is remembered for the session.

     DESKTOP ONLY, and not by choice: a phone has no hover, and a tap has to
     open the hide rather than describe it. */
  var peekCache = {};
  function peek(el, h) {
    if (!el || !h) return;
    if (!window.matchMedia || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var box = null;
    var wanted = false;

    function show(data) {
      if (!wanted || !data || !data.image) return;
      if (!box) {
        box = document.createElement('span');
        box.className = 'ns-pdp-pager__peek';
        box.setAttribute('aria-hidden', 'true');
        var img = document.createElement('img');
        img.src = data.image;
        img.alt = '';
        img.width = 84;
        img.loading = 'eager';
        var cap = document.createElement('span');
        cap.className = 'ns-pdp-pager__peek-title';
        cap.textContent = data.title || '';
        box.appendChild(img);
        box.appendChild(cap);
        el.appendChild(box);
      }
      /* Two frames, not one: the element has to be in the document with its
         starting styles applied before the class that animates it is added,
         or the browser collapses both into one paint and nothing moves.

         With a timer alongside them, because requestAnimationFrame does not
         fire at all while a page is not being painted — and a peek that only
         appears when the browser feels like animating is a peek that
         sometimes never appears. Whichever arrives first does the work. */
      var lit = false;
      var light = function () {
        if (lit) return;
        lit = true;
        if (wanted && box) box.classList.add('is-in');
      };
      requestAnimationFrame(function () {
        requestAnimationFrame(light);
      });
      setTimeout(light, 40);
    }

    function load() {
      wanted = true;
      if (peekCache[h]) {
        show(peekCache[h]);
        return;
      }
      var key = 'northsheep_peek:' + h;
      var saved = readJSON(key);
      if (saved) {
        peekCache[h] = saved;
        show(saved);
        return;
      }
      fetch(PRODUCT_BASE + h + '?view=ns-status', { credentials: 'same-origin' })
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (d) {
          if (!d || !d.image) return;
          peekCache[h] = d;
          writeJSON(key, d);
          show(d);
        })
        .catch(function () {});
    }

    function hide() {
      wanted = false;
      if (box) box.classList.remove('is-in');
    }

    el.addEventListener('mouseenter', load);
    el.addEventListener('focus', load);
    el.addEventListener('mouseleave', hide);
    el.addEventListener('blur', hide);
  }

  function paint(list, total, offset) {
    var i = list.indexOf(handle);
    if (i === -1) return false;

    var prev = i > 0 ? list[i - 1] : null;
    var next = i < list.length - 1 ? list[i + 1] : null;
    if (!prev && !next) return false;

    /* The list is one page of the collection, so the position inside it has
       to be counted from where that page began — otherwise the third hide on
       page two is announced as the third hide in the collection. */
    var shown = (offset || 0) + i + 1;
    var of = total || list.length;
    /* The position is only honest against the full collection when the list
       IS the full collection. A part-loaded grid knows its real total (the
       grid stamps it), so "99 of 256" stays true even though only 96 handles
       travelled. */
    if (countEl && showCount) countEl.textContent = label(root.getAttribute('data-label-position'), shown, of);

    if (prev) {
      prevEl.href = urlFor(prev);
      prevEl.hidden = false;
      prevEl.setAttribute('aria-label', root.getAttribute('data-label-previous') + ' — ' + label(root.getAttribute('data-label-position'), shown - 1, of));
    } else {
      prevEl.hidden = true;
    }
    if (next) {
      nextEl.href = urlFor(next);
      nextEl.hidden = false;
      nextEl.setAttribute('aria-label', root.getAttribute('data-label-next') + ' — ' + label(root.getAttribute('data-label-position'), shown + 1, of));
    } else {
      nextEl.hidden = true;
    }

    root.hidden = false;
    root.classList.add('is-ready');

    /* Walking sideways moves the shopper's place in the collection with them.
       Without this, flipping through ten hides and then using the breadcrumb
       would drop them back at the first one — the grid would restore the card
       they originally clicked, which is now ten hides behind. */
    var ret = readJSON(RETURN_KEY);
    if (ret && ret.handle && ret.handle !== handle && list.indexOf(ret.handle) !== -1) {
      ret.handle = handle;
      ret.ts = Date.now();
      writeJSON(RETURN_KEY, ret);
      /* ...and in that view's own copy (ns-grid-return.js, audit 4 Oct 2026). */
      var views = readJSON('northsheep_grid_returns');
      if (views && ret.url && views[ret.url]) {
        views[ret.url] = ret;
        writeJSON('northsheep_grid_returns', views);
      }
    }

    if (prev) peek(prevEl, prev);
    if (next) peek(nextEl, next);

    /* Every route onward marks the height, so the keyboard behaves like the
       buttons. Written on pointerdown as well as click: a long press on a
       phone can end as a context menu rather than a click. The mark carries
       the arrow's destination, so a press that never becomes a navigation
       cannot move some other page (see rememberScroll). */
    [[prevEl, prev], [nextEl, next]].forEach(function (pair) {
      var el = pair[0];
      if (!el || el.hidden || !pair[1]) return;
      var to = urlFor(pair[1]);
      function mark() {
        rememberScroll(to);
      }
      el.addEventListener('click', mark);
      el.addEventListener('pointerdown', mark);
    });

    /* The keyboard follows whatever is drawn NOW — see the one listener
       below. */
    keyPrev = prev ? urlFor(prev) : null;
    keyNext = next ? urlFor(next) : null;

    return true;
  }

  /* ---- the keyboard: ONE listener, on WINDOW (audit, 23 Sep 2026) ----

     Desktop keyboards only. Deliberately NOT a swipe: the gallery owns the
     horizontal axis on a phone, and two sideways gestures on one screen is
     how you end up on a hide you never picked.

     It used to be added to the DOCUMENT inside paint(), which broke twice:

     - Arriving from a collection, paint() runs as this file loads — before
       ns-product-modal-nav.js adds its own arrow keys for the zoomed photos.
       Listeners on one node run in the order they were added, so this one
       ran first, and its defaultPrevented check came too early to see the
       zoom claim the key. Measured: zoom open on a three-photo hide, one
       press of the right arrow, and the page left for the next product.
       On WINDOW it runs after every listener on the document, so a claimed
       key is seen as claimed; and a key pressed inside any open dialog (the
       zoom, the basket drawer) is not ours either.

     - A colour swap draws a new pager, but the old listener stayed, still
       holding the old product's neighbours: the arrow keys went on to the
       next hide after the colour the shopper had just left. */
  var keyPrev = null;
  var keyNext = null;
  window.addEventListener('keydown', function (e) {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
    var t = e.target;
    if (t && (t.isContentEditable || /^(input|textarea|select)$/i.test(t.tagName))) return;
    if (t && t.closest && t.closest('[role="dialog"], [aria-modal="true"], dialog, product-modal')) return;
    var to = e.key === 'ArrowRight' ? keyNext : e.key === 'ArrowLeft' ? keyPrev : null;
    if (!to) return;
    // Scrolled past the product (reviews, recently viewed): not ours, see productInView.
    if (!productInView()) return;
    rememberScroll(to);
    location.href = to;
  });

  function fetchPage(colUrl, want, page, acc) {
    var sep = colUrl.indexOf('?') === -1 ? '?' : '&';
    return fetch(colUrl + sep + 'view=ns-handles&page=' + page, { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (data) {
        if (!data || !data.handles) return acc;
        acc.handles = acc.handles.concat(data.handles);
        acc.total = data.total || acc.handles.length;
        /* Walk on only while the hide we are standing on has not been found.
           A collection of 256 is two requests at most, and the first one
           usually contains it. */
        if (acc.handles.indexOf(want) === -1 && page < data.pages && page < 6) {
          return fetchPage(colUrl, want, page + 1, acc);
        }
        return acc;
      })
      .catch(function () {
        return acc;
      });
  }

  function start() {
    var mine = ++generation;
    keyPrev = null;
    keyNext = null;

    root = document.querySelector('[data-ns-pager]');
    if (!root) {
      document.documentElement.classList.remove('ns-pager-space');
      return;
    }
    /* The head reserved a line for the pager on phones, before anything was
       known about this product. If no pager ends up being drawn — a hide
       reached from somewhere outside the list, with no collection to fall back
       on — the reserved line is given back rather than left standing empty. */
    var drawn = root;
    /* The breadcrumb section held the row from the first paint
       (.ns-pdp-breadcrumbs--pager, whenever it names a collection with more
       than this one product), so the late draw below moves nothing. Wherever
       it turns out there is nothing to draw, the row is given back. */
    var giveBack = function () {
      if (mine !== generation) return;
      document.documentElement.classList.remove('ns-pager-space');
      var held = root.closest('.ns-pdp-breadcrumbs--pager');
      if (held) held.classList.remove('ns-pdp-breadcrumbs--pager');
    };
    var settle = function () {
      setTimeout(function () {
        if (drawn.hidden) document.documentElement.classList.remove('ns-pager-space');
      }, 1200);
    };
    if (document.readyState === 'complete') settle();
    else window.addEventListener('load', settle);

    handle = root.getAttribute('data-product-handle');
    if (!handle) return;

    prevEl = root.querySelector('[data-ns-pager-prev]');
    nextEl = root.querySelector('[data-ns-pager-next]');
    countEl = root.querySelector('[data-ns-pager-count]');
    /* The counter is a merchant setting (owner, 20 Sep 2026: "in the winter i
       have sales constantly so the count is never really correct?"). It is
       accurate where it matters — the collections are built on "in stock", so a
       hide that sells leaves them, and a shopper arriving fresh gets a number
       that is right at that moment. What it cannot do is follow a sale that
       happens while ONE shopper is walking the list they arrived with. On a
       store where that is constant, the arrows still work perfectly without a
       number attached to them. */
    showCount = root.getAttribute('data-show-count') !== 'false';
    if (!showCount && countEl) countEl.remove();

    /* ---- 1. the list the shopper brought with them ---- */
    var stored = readJSON(LIST_KEY);
    if (stored && stored.handles && stored.handles.length) {
      /* Under a client-side filter the collection's own total and the page
         offset both describe a list the shopper is not looking at, so the list
         counts itself instead: "3 of 12 favourites" rather than "3 of 256". */
      var useTotal = stored.filtered ? stored.handles.length : stored.total;
      var useOffset = stored.filtered ? 0 : stored.offset || 0;
      if (paint(stored.handles, useTotal, useOffset)) return;
      /* In storage but not in this list: they have wandered off the rack they
         came from — a related product, a colour disc. Fall through and ask the
         collection this page actually belongs to. */
    }

    /* ---- 2. the collection answers for itself ---- */
    var colUrl = root.getAttribute('data-collection-url');
    if (!colUrl) {
      giveBack();
      return;
    }

    var cacheKey = FETCH_PREFIX + colUrl;
    var cached = readJSON(cacheKey);
    if (cached && cached.handles) {
      /* No offset on this path: the fetch walks the collection from its first
         product, so the index IS the position. */
      if (!paint(cached.handles, cached.total, 0)) giveBack();
      return;
    }

    /* After load, never during it: the arrows are an invitation, not part of
       the page's job. A swap while this is in flight makes its answer stale:
       it is still cached, but only the current start() may draw. */
    var want = handle;
    var go = function () {
      fetchPage(colUrl, want, 1, { handles: [], total: 0 }).then(function (acc) {
        if (!acc.handles.length) {
          giveBack();
          return;
        }
        writeJSON(cacheKey, acc);
        if (mine !== generation) return;
        if (!paint(acc.handles, acc.total, 0)) giveBack();
      });
    };
    if (document.readyState === 'complete') setTimeout(go, 0);
    else window.addEventListener('load', go);
  }

  start();

  /* A COLOUR SWAP IS A NEW PRODUCT WITHOUT A PAGE LOAD (audit, 23 Sep 2026).
     ns-colour-siblings.js hands the page to Dawn's product swap, which
     replaces <main> — this row included — and runs the scripts inside it
     again; this file then stops at the guard at the top. So the new row was
     never drawn: after a swap the pager simply vanished. The swap announces
     itself, and the row is drawn for the product now on screen. */
  document.addEventListener('ns:colour-swapped', start);
})();
